// view-tab.tsx — single tab in the views row. Operator paths beyond
// plain activation:
//   1. Hover → ⋯ menu (Rename · Duplicate · Delete · Open in BQ ·
//      Copy SQL · Copy as Sheets address · Open in Looker Studio)
//   2. Double-click name → inline rename
// The 'events' base view is the auto-created envelope view; we hide
// its destructive actions since it's not deletable.
//
// Extracted from DataSurfaceV3.tsx to keep the orchestrator file
// under a sane LOC budget. State is fully local (hover/menu/edit
// drafts) so prop surface stays minimal.

import React, { useEffect, useRef, useState } from 'react';
import type { BqView } from '../../api';
import { extractColumnNamesFromSql, tabMenuItemStyle } from './data-surface-utils';

export function ViewTab({ view, active, dataset, onActivate, onRename, onDuplicate, onDelete, onCopySql }: {
  view: BqView;
  active: boolean;
  dataset: string;
  onActivate: () => void;
  onRename: (next: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCopySql: () => void;
}): React.ReactElement {
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(view.name);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // When a tab becomes active and it's offscreen in the horizontally
  // scrollable tabs row, smoothly bring it into view. Operator with
  // 26 detected models doesn't have to manually pan.
  useEffect(() => {
    if (active && rootRef.current) {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [active]);
  useEffect(() => {
    if (!menuOpen) return;
    function close(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);
  const isBase = view.name === 'events';
  function commitRename(): void {
    const trimmed = draft.trim();
    setEditing(false);
    if (!trimmed || trimmed === view.name) { setDraft(view.name); return; }
    onRename(trimmed);
  }
  return (
    <div
      ref={rootRef}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        background: active ? 'var(--bg-elev)' : 'transparent',
        borderTop: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
        borderLeft: '1px solid var(--line)',
        borderRight: '1px solid var(--line)',
        marginBottom: -1,
        display: 'flex', alignItems: 'center',
      }}
    >
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
          onBlur={commitRename}
          onKeyDown={e => {
            if (e.key === 'Enter') commitRename();
            else if (e.key === 'Escape') { setDraft(view.name); setEditing(false); }
          }}
          style={{
            padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12.5,
            background: 'var(--bg)', border: '1px solid var(--accent-line)',
            borderRadius: 3, color: 'var(--ink)',
            margin: 2, minWidth: 140,
          }}
        />
      ) : (
        <button
          type="button"
          onClick={onActivate}
          onDoubleClick={() => { if (!isBase) { setDraft(view.name); setEditing(true); } }}
          title={(() => {
            const cols = extractColumnNamesFromSql(view.sql ?? '');
            const parts: string[] = [view.name];
            if (view.description) parts.push(view.description);
            if (cols.length > 0) parts.push(`Columns (${cols.length}): ${cols.slice(0, 12).join(', ')}${cols.length > 12 ? `, +${cols.length - 12} more` : ''}`);
            if (!isBase) parts.push('Double-click to rename');
            else parts.push('Base view — rename disabled');
            return parts.join('\n\n');
          })()}
          style={{
            padding: '8px 14px', fontSize: 12.5, fontFamily: 'var(--mono)',
            background: 'transparent',
            color: active ? 'var(--ink)' : 'var(--ink-3)',
            border: 'none',
            cursor: 'pointer',
            fontWeight: active ? 500 : 400,
            whiteSpace: 'nowrap',
          }}
        >{view.name}</button>
      )}
      {hover && !editing && !isBase && (
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
            title="Tab actions"
            style={{
              padding: '4px 6px', fontSize: 13, lineHeight: 1,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--ink-3)',
            }}
          >⋯</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4,
              background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
              boxShadow: '0 6px 20px oklch(0.18 0.01 250 / 0.20)',
              minWidth: 200, zIndex: 50,
              padding: 4, display: 'flex', flexDirection: 'column',
            }}>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setDraft(view.name); setEditing(true); }}
                style={tabMenuItemStyle}
              >Rename</button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDuplicate(); }}
                style={tabMenuItemStyle}
              >Duplicate</button>
              <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
              <a
                href={`https://console.cloud.google.com/bigquery?project=hub-mega-prod&p=hub-mega-prod&d=${encodeURIComponent(dataset)}&t=${encodeURIComponent(view.name)}&page=table`}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
                style={{ ...tabMenuItemStyle, textDecoration: 'none', display: 'block' }}
                title="Open this view in the BigQuery console (live data, can run new queries)"
              >↗ Open in BigQuery</a>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  const fqid = `hub-mega-prod.${dataset}.${view.name}`;
                  void navigator.clipboard.writeText(fqid);
                }}
                style={tabMenuItemStyle}
                title="Copy `hub-mega-prod.dataset.view` — paste into Google Sheets → Data → Connected Sheets → BigQuery"
              >📋 Copy as Sheets address</button>
              <a
                href={`https://lookerstudio.google.com/datasources/create?connectorId=2d&datasourceConfig=${encodeURIComponent(JSON.stringify({ projectId: 'hub-mega-prod', type: 'TABLE', datasetId: dataset, tableId: view.name }))}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
                style={{ ...tabMenuItemStyle, textDecoration: 'none', display: 'block' }}
                title="Create a new Looker Studio data source pre-filled with this BQ view"
              >↗ Open in Looker Studio</a>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onCopySql(); }}
                style={tabMenuItemStyle}
                title="Copy the view's SQL — paste into BQ console / dbt / Sheets"
              >Copy SQL</button>
              <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(); }}
                style={{ ...tabMenuItemStyle, color: 'var(--danger)' }}
              >Delete view…</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
